import React, { useMemo, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import Card, { CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import { AdminEmptyState, AdminNotice, AdminSkeletonRows } from '../../components/admin/AdminState';
import { useRole } from '../../context/RoleContext';
import { useAdminData } from '../../context/AdminDataContext';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/Table';

import { measureProductAction, trackProductInteraction } from '../../lib/productAnalytics';

const Companies: React.FC = () => {
  const { role, activeCompanyId, setActiveCompanyId } = useRole();
  const { clients, devices, logsByClient, fetchClientLogs, loading, error, createClient } = useAdminData();
  const [query, setQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [clientName, setClientName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [mutationSuccess, setMutationSuccess] = useState<string | null>(null);

  const companyRows = useMemo(
    () =>
      clients
        .map((client) => ({
          ...client,
          deviceCount: devices.filter((device) => device.client_id === client.id).length,
          logCount: logsByClient[client.id]?.length ?? 0,
        }))
        .filter((client) => [client.name, client.id].join(' ').toLowerCase().includes(query.toLowerCase())),
    [clients, devices, logsByClient, query]
  );

  const activeCompany = companyRows.find((company) => company.id === activeCompanyId) ?? companyRows[0] ?? null;

  React.useEffect(() => {
    if (!activeCompanyId && companyRows[0]?.id) {
      setActiveCompanyId(companyRows[0].id);
      return;
    }

    if (activeCompany?.id && !logsByClient[activeCompany.id]) {
      fetchClientLogs(activeCompany.id).catch(() => undefined);
    }
  }, [activeCompany?.id, activeCompanyId, companyRows, fetchClientLogs, logsByClient, setActiveCompanyId]);

  const handleCreateClient = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedName = clientName.trim();

    if (!trimmedName) {
      setMutationError('Client name is required.');
      return;
    }

    setSubmitting(true);
    setMutationError(null);
    setMutationSuccess(null);

    try {
      const client = await measureProductAction('company_create', () => createClient(trimmedName));
      setActiveCompanyId(client.id);
      setClientName('');
      setIsCreating(false);
      setMutationSuccess(`${client.name} was created.`);
    } catch (err) {
      setMutationError(err instanceof Error ? err.message : 'Failed to create client.');
    } finally {
      setSubmitting(false);
    }
  };

  if (role !== 'super_admin') {
    return (
      <Card className="border-[var(--admin-amber)] bg-[var(--admin-amber-soft)]">
        <CardHeader>
          <CardTitle>Super User Access Required</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-[var(--admin-muted)]">
          Company management is available to super users only.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <section className="admin-page-header">
        <div>
          <h1 className="text-3xl font-semibold">Companies</h1>
          <p className="mt-1 text-sm text-[var(--admin-muted)]">
            Backend-backed client portfolio from the admin API.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--admin-muted)]" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search clients"
              className="rounded-full border border-[var(--admin-border)] bg-white/80 py-2.5 pl-9 pr-4 text-sm"
            />
          </div>
          <button
            type="button"
            onClick={() => { if (!isCreating) trackProductInteraction('company_form_opened'); setIsCreating((current) => !current); }}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--admin-border)] bg-[var(--admin-text)] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:translate-y-[-1px]"
          >
            <Plus size={16} />
            Add Company
          </button>
        </div>
      </section>

      {isCreating ? (
        <Card className="border-[var(--admin-cyan)] bg-[var(--admin-surface-strong)]">
          <CardHeader>
            <CardTitle>Create Client</CardTitle>
            <p className="text-sm text-[var(--admin-muted)]">
              Creates a backend client through `POST /admin/api/registerClient`.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateClient} className="flex flex-col gap-3 md:flex-row md:items-start">
              <div className="flex-1">
                <label htmlFor="clientName" className="sr-only">
                  Client name
                </label>
                <input
                  id="clientName"
                  value={clientName}
                  onChange={(event) => setClientName(event.target.value)}
                  placeholder="Client name"
                  className="w-full rounded-2xl border border-[var(--admin-border)] bg-white px-4 py-3 text-sm"
                  disabled={submitting}
                />
                <p className="mt-2 text-xs text-[var(--admin-muted)]">
                  Authenticated Firebase admin credentials are required by the backend.
                </p>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="rounded-2xl bg-[var(--admin-cyan)] px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? 'Creating...' : 'Create Client'}
              </button>
              <button
                type="button"
                onClick={() => setIsCreating(false)}
                className="rounded-2xl border border-[var(--admin-border)] bg-white/75 px-5 py-3 text-sm font-semibold text-[var(--admin-muted)]"
                disabled={submitting}
              >
                Cancel
              </button>
            </form>
          </CardContent>
        </Card>
      ) : null}

      {mutationError ? <AdminNotice tone="error">{mutationError}</AdminNotice> : null}

      {mutationSuccess ? <AdminNotice tone="success">{mutationSuccess}</AdminNotice> : null}

      {error ? <AdminNotice tone="error" title="Failed to load clients">{error}</AdminNotice> : null}

      <section className="admin-page-kpis">
        <Card className="bg-[var(--admin-surface-strong)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-[0.16em] text-[var(--admin-muted)]">Clients</CardTitle>
          </CardHeader>
          <CardContent className="text-3xl font-semibold">{clients.length}</CardContent>
        </Card>
        <Card className="bg-[var(--admin-surface-strong)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-[0.16em] text-[var(--admin-muted)]">Portfolio Devices</CardTitle>
          </CardHeader>
          <CardContent className="text-3xl font-semibold">{devices.length}</CardContent>
        </Card>
        <Card className="bg-[var(--admin-surface-strong)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-[0.16em] text-[var(--admin-muted)]">Logs Loaded</CardTitle>
          </CardHeader>
          <CardContent className="text-3xl font-semibold">
            {Object.values(logsByClient).reduce((sum, rows) => sum + rows.length, 0)}
          </CardContent>
        </Card>
        <Card className="bg-[var(--admin-surface-strong)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-[0.16em] text-[var(--admin-muted)]">Status</CardTitle>
          </CardHeader>
          <CardContent className="text-sm font-semibold">{loading ? 'Loading API data' : 'Connected'}</CardContent>
        </Card>
      </section>

      <section className="grid gap-6 xl:grid-cols-[minmax(0,1.55fr)_minmax(22rem,0.8fr)]">
        <Card className="overflow-hidden">
          <CardHeader>
            <CardTitle>Client Portfolio</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <AdminSkeletonRows /> : null}
            {!loading && companyRows.length === 0 ? (
              <AdminEmptyState title="No clients found">
                Adjust the search query or create the first client through the onboarding form.
              </AdminEmptyState>
            ) : null}
            {!loading && companyRows.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Client ID</TableHead>
                    <TableHead>Devices</TableHead>
                    <TableHead>Logs Loaded</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {companyRows.map((company) => (
                    <TableRow
                      key={company.id}
                      data-state={company.id === activeCompany?.id ? 'selected' : undefined}
                      className="cursor-pointer"
                      onClick={() => { trackProductInteraction('company_switch'); setActiveCompanyId(company.id); }}
                    >
                      <TableCell className="font-semibold">{company.name}</TableCell>
                      <TableCell className="text-xs text-[var(--admin-muted)]">{company.id}</TableCell>
                      <TableCell>{company.deviceCount}</TableCell>
                      <TableCell>{company.logCount}</TableCell>
                      <TableCell>
                        <Badge variant="success">Available</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : null}
          </CardContent>
        </Card>

        <Card className="sticky top-24 self-start">
          <CardHeader>
            <CardTitle>Company Inspector</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {activeCompany ? (
              <>
                <div className="rounded-2xl bg-[#171513] px-4 py-4 text-stone-100">
                  <p className="text-xs uppercase tracking-[0.18em] text-stone-400">Selected Client</p>
                  <p className="mt-2 text-2xl font-semibold text-stone-50">{activeCompany.name}</p>
                  <p className="mt-1 text-sm text-stone-400">{activeCompany.id}</p>
                </div>

                <div className="grid gap-3">
                  <div className="rounded-2xl border border-[var(--admin-border)] bg-white/70 px-4 py-3">
                    <p className="text-xs uppercase tracking-[0.16em] text-[var(--admin-muted)]">Device Count</p>
                    <p className="mt-1 font-semibold">{activeCompany.deviceCount}</p>
                  </div>
                  <div className="rounded-2xl border border-[var(--admin-border)] bg-white/70 px-4 py-3">
                    <p className="text-xs uppercase tracking-[0.16em] text-[var(--admin-muted)]">Logs Loaded</p>
                    <p className="mt-1 font-semibold">{activeCompany.logCount}</p>
                  </div>
                  <div className="rounded-2xl border border-[var(--admin-border)] bg-white/70 px-4 py-3">
                    <p className="text-xs uppercase tracking-[0.16em] text-[var(--admin-muted)]">Writable Actions</p>
                    <p className="mt-1 text-sm text-[var(--admin-muted)]">
                      Client creation is wired to the backend. Device and payroll mutations are handled in their modules.
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <AdminEmptyState title="No client selected">Select a client to inspect backend-backed details.</AdminEmptyState>
            )}
          </CardContent>
        </Card>
      </section>
    </div>
  );
};

export default Companies;
